import Vue from 'vue';

import { http } from '@/http';
import Modal, { ModalLinePrimary } from '@/components/Modal';
import Button from '@/components/Controls/Button';
import Input from '@/components/Controls/Input';
import { CmsError } from '@/http/CmsError';
import { AuthParams } from './types';
import { RootElementAttrsHolder } from '@/plugins/RootElementAttrsPlugin';

const defaultAuthParams: AuthParams = {
    checkAuth: '/design-project/auth/checkAuth.json',
    auth: '/design-project/auth/auth.json',
    redirectAfterAuth: '/design-project/docs/personal-data.act',
};

interface AuthWidgetData {
    active: boolean;
    password: string;
    login: string;
    fieldErrors: Record<string, any>;
    formErrors: ConcatArray<never>;
    errorMessage: string | null;
    authParams: AuthParams;
}

export function createAuthWidget(el: Element) {
    const rootAttrs = new RootElementAttrsHolder(el);
    return new Vue({
        el,
        name: 'AuthWidget',
        rootAttrs,
        data(): AuthWidgetData {
            return {
                active: false,
                password: '',
                login: '',
                fieldErrors: {},
                formErrors: [],
                errorMessage: null,
                authParams: Object.assign({}, defaultAuthParams),
            };
        },
        methods: {
            /**
             * Переопределяет параметры авторизации
             *
             * @param {*} authParams
             * @returns
             */
            withParams(authParams: AuthParams) {
                this.authParams = Object.assign({}, defaultAuthParams, authParams);
                return this;
            },
            /**
             * Оповещает слушателей о результате авторизации
             *
             * @note вызывать только с success либо cancel
             */
            emitAuthResult(state: { state: string }) {
                this.$emit('authResult', state);
            },
            /**
             * Редирект в ЛК
             */
            makeRedirect() {
                window.location.href = this.authParams.redirectAfterAuth;
            },
            /**
             * Проверяет авторизован ли юзер
             */
            async isAuthorized() {
                try {
                    await http.get(this.authParams.checkAuth);
                    return true;
                } catch (error) {
                    return false;
                }
            },
            /**
             * Процедура авторизации с переходом в ЛК
             *
             * @returns
             */
            async goToLK() {
                const isAuthorized = await this.isAuthorized();

                if (isAuthorized) {
                    return this.makeRedirect();
                }

                this.show();

                this.$once('authResult', ({ state }: { state: string }) => {
                    if (state === 'success') {
                        this.makeRedirect();
                    }
                });
            },
            /**
             * Показывает форму
             */
            show() {
                this.active = true;
            },
            /**
             * Закрывает форму
             */
            close() {
                this.active = false;
                this.emitAuthResult({ state: 'cancel' });
            },
            /**
             * Отправляет форму
             */
            async submit(e: SubmitEvent) {
                e.preventDefault();

                if (!this.validateForm()) {
                    return;
                }

                this.$wait.start('formSend');

                try {
                    await http({
                        url: this.authParams.auth,
                        method: 'POST',
                        data: {
                            login: this.login,
                            password: this.password,
                        },
                    });
                    this.emitAuthResult({ state: 'success' });
                    this.password = '';
                    this.login = '';
                    this.active = false;
                } catch (error) {
                    this.$errorLog(error);
                    // сбрасываем все поля ошибок, чтобы отображались только актуальные
                    this.resetErrors();

                    if (error instanceof CmsError) {
                        const data = error.details.data || {};
                        const { fieldErrors, formErrors, message } = data;
                        this.fieldErrors = fieldErrors || {};
                        this.formErrors = formErrors || [];
                        this.errorMessage = message || null;
                    } else {
                        this.errorMessage = this.getErrorMessage(error);
                    }
                } finally {
                    this.$wait.end('formSend');
                }
            },
            /**
             * Плучает сообщение ошибки
             */
            getErrorMessage(error: any) {
                if (error.response) {
                    const message = error.message ? `(${error.message})` : '';
                    return `Сервер отклонил запрос ${message}`;
                } else if (error.request) {
                    return 'Сервер не отвечает';
                } else {
                    return 'Неизвестная ошибка';
                }
            },
            /**
             * Сбрасывает ошибки
             */
            resetErrors() {
                this.fieldErrors = {};
                this.formErrors = [];
                this.errorMessage = null;
            },
            /**
             * Сбрасывает ошибку
             */
            deleteError(errorKey: string) {
                if (this.fieldErrors[errorKey]) {
                    this.$delete(this.fieldErrors, errorKey);
                }
            },
            validateForm() {
                let isValid = true;

                if (!this.login) {
                    this.$set(this.fieldErrors, 'login', 'Введите логин');
                    isValid = false;
                }

                if (!this.password) {
                    this.$set(this.fieldErrors, 'password', 'Введите пароль');
                    isValid = false;
                }

                return isValid;
            },
        },

        render(h) {
            return (
                <Modal
                    active={this.active}
                    onClose={this.close}
                    swipeDownMaxDistance={150}
                >
                    <ModalLinePrimary class="text-mobile-center pb-10">
                        Авторизация
                    </ModalLinePrimary>

                    <div class="feedback-container__error-pre feedback-error">
                        {this.errorMessage}
                    </div>

                    <form
                        onSubmit={this.submit}
                        class="grid grid--y-default"
                        autocomplete="off"
                    >
                        <div class="col">
                            <Input
                                v-model={this.login}
                                placeholder="Логин"
                                error={this.fieldErrors.login}
                                onInput={() => {
                                    this.deleteError('login');
                                }}
                            />
                        </div>

                        <div class="col">
                            <Input
                                v-model={this.password}
                                placeholder="Пароль"
                                type="password"
                                autocomplete="new-password"
                                error={this.fieldErrors.password}
                                onInput={() => {
                                    this.deleteError('password');
                                }}
                            />
                        </div>

                        <div class="feedback-error feedback-container__error-after">
                            {[].concat(this.formErrors).join(', ')}
                        </div>

                        <div class="col">
                            <Button
                                type="submit"
                                modifiers={['primary', 'block']}
                                loading={this.$wait.waiting('formSend')}
                                class="mt-10"
                            >
                                Войти
                            </Button>
                        </div>
                    </form>
                </Modal>
            );
        },
    });
}
